import React, { useState } from "react";
import "aos/dist/aos.css";
import { MdOutlinePassword } from "react-icons/md";
import { toast } from "react-toastify";
import { buttonStyleSecondarySm } from "../../help/Utils";
import { changePassword } from "../../API/auth";
import ButtonLoadingLabel from "../../components/ButtonLoadingLabel";

const ChangePassword: React.FC = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      toast.error("All fields are required");
      return;
    }

    const { newPassword } = passwordData;
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      toast.error("Password must contain at least one lowercase letter");
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error("Password must contain at least one number");
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      toast.error("Password must contain at least one special character");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error("New password must be different from current password");
      return;
    }

    try {
      setIsUpdating(true);
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      toast.success("Password updated successfully.");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Failed to update password";
      toast.error(errorMessage);
      console.error("Error updating password:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <h5 className="text-xl font-normal mb-5">Change Password</h5>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="text-[14px] text-[#374151]">Current Password</label>
          <input
            type="password"
            name="currentPassword"
            className="block border w-full mt-1 p-2 rounded-md"
            placeholder="********"
            value={passwordData.currentPassword}
            onChange={handleChange}
            disabled={isUpdating}
          />
        </div>
        <div className="mb-3">
          <label className="text-[14px] text-[#374151]">New Password</label>
          <input
            type="password"
            name="newPassword"
            className="block border w-full mt-1 p-2 rounded-md"
            placeholder="********"
            value={passwordData.newPassword}
            onChange={handleChange}
            disabled={isUpdating}
          />
          <p className="text-xs text-gray-500 mt-1">
            At least 8 characters, with one uppercase, one lowercase, one number,
            and one special character.
          </p>
        </div>
        <div className="mb-5">
          <label className="text-[14px] text-[#374151]">Confirm Password</label>
          <input
            type="password"
            name="confirmPassword"
            className="block border w-full mt-1 p-2 rounded-md"
            placeholder="********"
            value={passwordData.confirmPassword}
            onChange={handleChange}
            disabled={isUpdating}
          />
        </div>

        <button
          type="submit"
          className="flex items-center justify-center text-center w-full"
          style={buttonStyleSecondarySm}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <ButtonLoadingLabel label="Updating" />
          ) : (
            <>
              <MdOutlinePassword className="me-4 text-[24px]" />
              Update Password
            </>
          )}
        </button>
      </form>
    </>
  );
};

export default ChangePassword;
